import { toZonedTime } from "date-fns-tz";

// Helper function to format dates with timezone conversion
export const formatDate = (
  date: string | Date | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "2-digit",
  }
) => {
  if (!date) return "";
  let localDate: Date;
  if (typeof date === "string") {
    localDate = new Date(date);
  } else if (date instanceof Date) {
    localDate = date;
  } else {
    return "";
  }

  return localDate.toLocaleDateString("es-MX", options);
};

export const displayISODate = (isoDate: string) => {
  // Parse the date with the offset
  const zonedDate = toZonedTime(isoDate, "America/Mexico_City");

  // Format it to match the Spanish format
  return zonedDate.toLocaleDateString("es-MX", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour12: false,
  });
};

export interface ExpiryInfo {
  daysUntilExpiry: number;
  isExpired: boolean;
  daysSinceExpiry: number;
  status: "expired" | "expires_today" | "expires_soon" | "valid";
}

/**
 * Calculates expiry information for a given due date
 * @param dueDate - The due date as string or Date object
 * @param currentDate - Optional current date for testing (defaults to today)
 * @returns ExpiryInfo object with expiry details
 */
export const getExpiryInfo = (
  dueDate: string | Date,
  currentDate?: Date
): ExpiryInfo => {
  const today = currentDate || new Date();
  let targetDate: Date;

  // Parse the due date
  if (typeof dueDate === "string") {
    targetDate = new Date(dueDate);
  } else {
    targetDate = dueDate;
  }

  // Reset time to start of day for accurate day comparison
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const dueDateStart = new Date(
    targetDate.getFullYear(),
    targetDate.getMonth(),
    targetDate.getDate()
  );

  // Calculate difference in milliseconds and convert to days
  const diffInMs = dueDateStart.getTime() - todayStart.getTime();
  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

  const isExpired = diffInDays < 0;
  const daysUntilExpiry = isExpired ? 0 : diffInDays;
  const daysSinceExpiry = isExpired ? Math.abs(diffInDays) : 0;

  // Determine status
  let status: ExpiryInfo["status"];
  if (isExpired) {
    status = "expired";
  } else if (diffInDays === 0) {
    status = "expires_today";
  } else if (diffInDays <= 7) {
    status = "expires_soon";
  } else {
    status = "valid";
  }

  return {
    daysUntilExpiry,
    isExpired,
    daysSinceExpiry,
    status,
  };
};

/**
 * Gets a human-readable expiry message in Spanish
 * @param dueDate - The due date as string or Date object
 * @param currentDate - Optional current date for testing (defaults to today)
 * @returns Human-readable expiry message
 */
export const getExpiryMessage = (
  dueDate: string | Date,
  currentDate?: Date
): string => {
  const info = getExpiryInfo(dueDate, currentDate);

  if (info.isExpired) {
    if (info.daysSinceExpiry === 1) {
      return "Venció ayer";
    }
    return `Venció hace ${info.daysSinceExpiry} días`;
  }

  if (info.daysUntilExpiry === 0) {
    return "Vence hoy";
  }

  if (info.daysUntilExpiry === 1) {
    return "Vence mañana";
  }

  return `Vence en ${info.daysUntilExpiry} días`;
};
